import {
  syncWatchV2,
  WatchSyncV2Operation,
  WatchSyncV2Request,
  WatchSyncV2Response,
  WatchSyncV2State,
} from '../api/backendAPI';

const STORAGE_PREFIX = 'ott_watch_sync_v2:';
const MAX_BATCH_SIZE = 200;
const FLUSH_DELAY_MS = 750;

export interface WatchSyncV2Credentials {
  deviceId: string;
  activationPassword: string;
  profileId: string;
}

export interface WatchSyncStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
  readonly length?: number;
  key?(index: number): string | null;
}

interface PersistedWatchSyncV2 {
  revision: string;
  queue: WatchSyncV2Operation[];
  snapshot: WatchSyncV2State | null;
}

type WatchSyncTransport = (request: WatchSyncV2Request) => Promise<WatchSyncV2Response>;
type OperationWithoutId = WatchSyncV2Operation extends infer Operation
  ? Operation extends WatchSyncV2Operation
    ? Omit<Operation, 'operationId'>
    : never
  : never;

function emptyState(): PersistedWatchSyncV2 {
  return { revision: '0', queue: [], snapshot: null };
}

function isOperation(value: unknown): value is WatchSyncV2Operation {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<WatchSyncV2Operation>;
  return typeof candidate.operationId === 'string'
    && candidate.operationId.length >= 8
    && typeof candidate.type === 'string'
    && !!candidate.payload
    && typeof candidate.payload === 'object';
}

function loadState(storage: WatchSyncStorage, key: string): PersistedWatchSyncV2 {
  try {
    const raw = storage.getItem(key);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<PersistedWatchSyncV2>;
    return {
      revision: /^\d+$/.test(String(parsed.revision || '')) ? String(parsed.revision) : '0',
      queue: Array.isArray(parsed.queue) ? parsed.queue.filter(isOperation) : [],
      snapshot: parsed.snapshot?.fullSnapshot === true ? parsed.snapshot : null,
    };
  } catch {
    return emptyState();
  }
}

function operationId(): string {
  const cryptoApi = globalThis.crypto;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  const random = Math.random().toString(36).slice(2);
  return `${Date.now().toString(36)}-${random}-${random}`;
}

export class WatchSyncV2Repository {
  private credentials: WatchSyncV2Credentials;
  private readonly storage: WatchSyncStorage;
  private readonly transport: WatchSyncTransport;
  private readonly autoFlush: boolean;
  private readonly storageKey: string;
  private state: PersistedWatchSyncV2;
  private inFlight: Promise<WatchSyncV2State> | null = null;
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    credentials: WatchSyncV2Credentials,
    storage: WatchSyncStorage = localStorage,
    transport: WatchSyncTransport = syncWatchV2,
    autoFlush = true,
  ) {
    this.credentials = credentials;
    this.storage = storage;
    this.transport = transport;
    this.autoFlush = autoFlush;
    this.storageKey = `${STORAGE_PREFIX}${credentials.profileId}`;
    this.state = loadState(storage, this.storageKey);
  }

  updateCredentials(credentials: WatchSyncV2Credentials): void {
    if (credentials.profileId !== this.credentials.profileId) {
      throw new Error('Cannot change profileId on an existing Watch Sync repository');
    }
    this.credentials = credentials;
  }

  getSnapshot(): WatchSyncV2State | null {
    return this.state.snapshot;
  }

  getPendingOperations(): readonly WatchSyncV2Operation[] {
    return this.state.queue;
  }

  enqueue(operation: OperationWithoutId): string {
    const queued = { ...operation, operationId: operationId() } as WatchSyncV2Operation;
    this.state.queue.push(queued);
    this.persist();
    if (this.autoFlush) this.scheduleFlush();
    return queued.operationId;
  }

  async syncNow(): Promise<WatchSyncV2State> {
    if (this.inFlight) return this.inFlight;
    this.inFlight = this.performSync();
    try {
      return await this.inFlight;
    } finally {
      this.inFlight = null;
    }
  }

  async clearRemote(): Promise<WatchSyncV2State> {
    const clearOperationId = this.enqueue({ type: 'watch.clear', payload: {} });
    const snapshot = await this.syncNow();
    if (this.state.queue.some((operation) => operation.operationId === clearOperationId)) {
      throw new Error('Backend did not confirm watch.clear');
    }
    if (snapshot.progress.length || snapshot.history.length || snapshot.favorites.length) {
      throw new Error('Backend confirmed watch.clear with a non-empty snapshot');
    }
    return snapshot;
  }

  dispose(): void {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = null;
  }

  private async performSync(): Promise<WatchSyncV2State> {
    let mustFetch = true;
    let latest = this.state.snapshot;

    for (let batchNumber = 0; batchNumber < 20; batchNumber += 1) {
      const operations = this.state.queue.slice(0, MAX_BATCH_SIZE);
      if (!mustFetch && operations.length === 0) break;
      mustFetch = false;

      const response = await this.transport({
        ...this.credentials,
        sinceRevision: this.state.revision,
        operations,
      });
      if (response.profileId !== this.credentials.profileId || response.state.fullSnapshot !== true) {
        throw new Error('Invalid Watch Sync V2 response');
      }

      const confirmed = new Set(response.confirmedOperations.map((item) => item.operationId));
      this.state.queue = this.state.queue.filter((operation) => !confirmed.has(operation.operationId));
      this.state.revision = response.state.syncRevision;
      this.state.snapshot = response.state;
      latest = response.state;
      this.persist();

      if (operations.length > 0 && !operations.some((operation) => confirmed.has(operation.operationId))) {
        throw new Error('Backend did not confirm any queued Watch Sync V2 operation');
      }
    }

    if (!latest) throw new Error('Watch Sync V2 returned no snapshot');
    if (this.autoFlush && this.state.queue.length > 0) this.scheduleFlush();
    return latest;
  }

  private scheduleFlush(): void {
    if (this.flushTimer) clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.syncNow().catch(() => {
        // Offline and transient errors intentionally retain the durable queue.
      });
    }, FLUSH_DELAY_MS);
  }

  private persist(): void {
    try {
      this.storage.setItem(this.storageKey, JSON.stringify(this.state));
    } catch {
      // Playback remains local when persistent browser storage is unavailable.
    }
  }
}

const repositories = new Map<string, WatchSyncV2Repository>();

export function getWatchSyncV2Repository(credentials: WatchSyncV2Credentials): WatchSyncV2Repository {
  const existing = repositories.get(credentials.profileId);
  if (existing) {
    existing.updateCredentials(credentials);
    return existing;
  }
  const repository = new WatchSyncV2Repository(credentials);
  repositories.set(credentials.profileId, repository);
  return repository;
}

export function clearAllWatchSyncV2LocalState(storage: WatchSyncStorage = localStorage): void {
  repositories.forEach((repository) => repository.dispose());
  repositories.clear();
  if (typeof storage.length !== 'number' || typeof storage.key !== 'function') return;
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  keys.forEach((key) => storage.removeItem(key));
}
